import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation, registerQuery } from '@adornis/baseql/metadata/register.js';
import { context, serverContext } from '@adornis/baseql/server/context.js';
import { runInContext } from '@adornis/baseql/server/server.js';
import { type BaseQLSelectionSet, type NarrowedEntity, selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import type { NarrowedEntityData } from '@adornis/bh-util/selectionset-types.js';
import { Timeframe } from '@adornis/bh-timeframes/entities/Timeframe.js';
import {
  getCurrentUserServer,
  isDefined,
  loadRawEntityDataByIDOrFail,
  loadRawEntityDataByIDs,
  loadRawEntityDataByQuery,
  loadRawMongoData,
  saveEntityData,
} from '@adornis/bh-util/util.js';
import { BasicProduct } from '@adornis/payments/db/basic-product.js';
import { ChosenProduct } from '@adornis/payments/db/chosenProduct.js';
import { Payment } from '@adornis/payments/db/payments.js';
import { Price } from '@adornis/payments/db/prices.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { assignRoleToUser } from '@adornis/users/server/roles.js';
import { generateLoginToken } from '@adornis/users/server/token.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { MultipleValidationError } from '@adornis/validation/errors/MultipleValidationError.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import type Stripe from 'stripe';
import { areTimeSlotOverlapping } from '../datetime/generate-start-times-for-multiple-overlapping-timeslots.js';
import { type IDateTimeslot, TimeSlot } from '../datetime/time-slot.js';
import { getDbPluginForScope } from '../db-plugins.js';
import { BHPayment } from '../entities/BHPayment.js';
import { Bundle } from '../entities/Bundle.js';
import { BundlePrice } from '../entities/BundlePrice.js';
import { Einzelberatung } from '../entities/einzelberatung.js';
import { Participation } from '../entities/Participation.js';
import { ParticipationEinzelberatung } from '../entities/ParticipationEinzelberatung.js';
import { ParticipationWorkshop } from '../entities/ParticipationWorkshop.js';
import { Product } from '../entities/products.js';
import {
  BookingResult,
  RegistrationData,
  RegistrationEinzelberatungData,
  RegistrationProductData,
  RegistrationWorkshopData,
} from '../entities/RegistrationDataBuffer.js';
import { getMergedProductSettings } from '../entities/settings.js';
import { UserStammdaten } from '../entities/stammdaten.js';
import { User } from '../entities/user.js';
import { Workshop } from '../entities/workshop.js';
import { ParticipationState } from '../enums/enums.js';
import { Role } from '../enums/roles.js';
import { BlockSignInHoursInAdvanceError } from '../errors/BlockSignInHoursInAdvanceError.js';
import { InvalidInstitutionIDError } from '../errors/InvalidInstitutionIDError.js';
import { RegistrationAuthorizationNeededError } from '../errors/RegistrationAuthorizationNeededError.js';
import { RegistrationEmailNotConfirmedError } from '../errors/RegistrationEmailNotConfirmedError.js';
import { RegistrationInvalidError } from '../errors/RegistrationInvalidError.js';
import { RegistrationMaintenanceError } from '../errors/RegistrationMaintenanceError.js';
import { RegistrationNotYetOpenError } from '../errors/RegistrationNotYetOpenError.js';
import { RegistrationStammdatenIncompleteError } from '../errors/RegistrationStammdatenIncompleteError.js';
import { RegistrationSuspendedError } from '../errors/RegistrationSuspendedError.js';
import { TimeslotsAreoverlappingError } from '../errors/TimeslotsAreoverlappingError.js';
import {
  canActivelyRegisterForProduct,
  canSignInForProduct,
  isRegistrationNotYetOpen,
  lowPriorityMayActivelyRegister,
  nachrueckPossible,
} from '../logic/participation.js';
import { notifyBestaetigungWorkshopRegistrierung } from '../notification/bestaetigung-workshop-registrierung.js';
import { sendEinzelberatungMeetingInfoMailsIfAlreadySent } from '../notification/einzelberatung-meeting-infos.js';
import { sendMeetingInfoMailsIfAlreadySent } from '../notification/meeting-infos.js';
import { createPayment } from '../payment.js';
import { getParticipationTimespan } from '../server/logic/Product.js';
import { failWhenSuspended } from '../server/logic/participation.js';
import { mongo } from '../server/mongo.js';
import { notifyUserPermissionChange } from '../server/user-change-notifier.js';
import { getSelectedBundles, getValidBundlesForProducts } from './bundle.js';
import { getParticipationsByIDs, countWaitingByWorkshopServer } from './participation.js';
import { loadUserEntryRequirementsStatus } from './product-entry-requirement.js';
import { getWaitinglistPriorityForUserForProduct } from './product-waitinglist-priority-rule.js';
import { getStammdatenByUserAndInstitution } from './stammdaten.js';
import { getUserByID, ensureStripeCustomerForUser } from './users.js';
import { calculateWorkshopLeftCapacity } from './workshop.js';
import { getInstitutionByID } from './institution.js';
import { getProductByID } from './product.js';
import { getBHPaymentByID } from './payment.js';

async function getAndAssertInstitution(institutionID: string) {
  if (!institutionID) throw new RegistrationInvalidError();

  const institution = await getInstitutionByID(institutionID)({ _id: 1, maintenanceMode: 1 });
  if (!institution) throw new InvalidInstitutionIDError(institutionID);
  if (institution.maintenanceMode) throw new RegistrationMaintenanceError();

  return institution;
}

export async function assertBookingEmailConfirmed(
  userID: string,
  ctx: { serverContext?: boolean; impersonatingUser?: string | null },
): Promise<void> {
  if (ctx.serverContext || ctx.impersonatingUser) return;

  const user = await mongo()
    .collection<User>(User._collectionName)
    .findOne({ _id: userID }, { projection: { emailConfirmed: 1 } });
  if (user?.emailConfirmed !== true) throw new RegistrationEmailNotConfirmedError();
}

export async function assertWorkshopRegistrationOpen(
  productIDs: Maybe<string[]>,
  ctx: { serverContext?: boolean; impersonatingUser?: string | null },
): Promise<void> {
  if (ctx.serverContext || ctx.impersonatingUser) return;

  if (!productIDs?.length) return;

  const products = await loadRawEntityDataByIDs(Product, productIDs, { _id: 1, _class: 1, timeframeID: 1 });
  const timeframes = await loadRawEntityDataByIDs(
    Timeframe,
    products
      .filter(product => product._class === Workshop._class)
      .map(product => product.timeframeID)
      .filter(isDefined),
    { from: 1, isRegistrationPossibleBesidesTimeframe: 1 },
  );
  if (timeframes.some(timeframe => isRegistrationNotYetOpen(timeframe))) throw new RegistrationNotYetOpenError();
}

export async function _book(
  user: NarrowedEntity<
    User,
    {
      _id: 1;
      email: 1;
      title: 1;
      username: 1;
      firstName: 1;
      lastName: 1;
      whatsappNotification: 1;
      suspentions: { _id: 1; institutionID: 1 };
      whatsappPhone: 1;
    }
  >,
  productData: Maybe<RegistrationProductData[]>,
  paymentMethod: Maybe<string>,
) {
  logger.info({ productData, paymentMethod }, '_book');
  // check nevertheless because public API
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!productData) throw new RegistrationInvalidError();

  const productIDs = productData.map(d => d.productID as string);
  const products = await loadRawEntityDataByIDs(Product, productIDs, {
    _id: 1,
    _class: 1,
    leftCapacity: 1,
    hasPrice: 1,
    prices: { unitAmount: 1 },
    institutionID: 1,
    settings: {
      allowSubscribingToWaitinglistWithoutMeetingRequirements: 1,
      inheritedAllowSubscribingToWaitinglistWithoutMeetingRequirements: 1,
    },
  });
  const foundProductIDs = new Set(products.map(product => product._id));
  if (productIDs.some(productID => !foundProductIDs.has(productID))) throw new RegistrationInvalidError();

  const mergedProductSettingsCache = new Map<string, Promise<any>>();
  const getMergedProductSettingsCached = (
    productID: string,
    fields: Parameters<typeof getMergedProductSettings>[1],
  ) => {
    const key = productID + '|' + [...fields].sort().join(',');
    const cached = mergedProductSettingsCache.get(key);
    if (cached) return cached;
    const promise = getMergedProductSettings(productID, fields);
    mergedProductSettingsCache.set(key, promise);
    return promise;
  };

  await Promise.all(
    products.map(async product => {
      (product as any).settings = await getMergedProductSettingsCached(product._id, [
        'allowSubscribingToWaitinglistWithoutMeetingRequirements',
      ]);
    }),
  );

  const productsToBuy = products.filter(product => {
    if (product._class === Workshop._class) {
      const workshop = product as typeof product & NarrowedEntityData<Workshop, { leftCapacity: 1 }>;
      if (workshop.leftCapacity === 0) return false;
    }
    return true;
  });

  const institutionID = products[0]?.institutionID;
  if (!institutionID) throw new Error('id should be defined');

  if (products.some(product => product.institutionID !== institutionID))
    throw new Error('ordering products from more than one institution at the same time is not supported');

  const institution = await getAndAssertInstitution(institutionID);
  const userID = user._id;

  const paymentPlugin = getDbPluginForScope('payment', institutionID);

  // The registration form validates the stammdaten client-side, but the booking steps are
  // URL-driven and therefore bypassable. Enforce the same required-field check server-side so a
  // user can never book without having filled in the mandatory Pflichtfelder.
  const rawStammdaten = await getStammdatenByUserAndInstitution(user._id, institutionID)(UserStammdaten.allFields);
  if (!rawStammdaten) throw new Error('stammdaten not found');

  const stammdatenEntity = constructValue(rawStammdaten) as UserStammdaten;
  try {
    // mirrors the client form controller's validate() options exactly so the server is never
    // stricter than the registration form the user actually filled out.
    await stammdatenEntity.validate({ validateOmitted: true, aggregateErrors: true });
  } catch (error) {
    if (error instanceof ValidationError || error instanceof MultipleValidationError) {
      logger.warn({ userID: user._id, institutionID, error }, 'booking blocked: required stammdaten fields missing');
      throw new RegistrationStammdatenIncompleteError();
    }
    throw error;
  }

  const stammdatenPlugin = getDbPluginForScope('registration::stammdaten', institutionID);
  const customValidationError = stammdatenPlugin.customValidate?.(stammdatenEntity);
  if (customValidationError) {
    logger.warn(
      { userID: user._id, institutionID, customValidationError },
      'booking blocked: stammdaten failed custom validation',
    );
    throw new RegistrationStammdatenIncompleteError();
  }

  const requiredExtraUserFields =
    getDbPluginForScope('registration::user-data', institutionID).requiredExtraUserFields ?? [];
  if (requiredExtraUserFields.length > 0) {
    const projection = Object.fromEntries(requiredExtraUserFields.map(field => [field, 1 as const]));
    const [userData]: Record<string, unknown>[] = await loadRawEntityDataByIDs(User, [user._id], projection);
    const missingFields = requiredExtraUserFields.filter(field => {
      const value = userData?.[field];
      return value === undefined || value === null || value === '';
    });
    if (missingFields.length > 0) {
      logger.warn({ userID: user._id, institutionID, missingFields }, 'booking blocked: required user fields missing');
      throw new RegistrationStammdatenIncompleteError();
    }
  }

  // handle suspensions for maybe already existing users
  if (user.suspentions.map(suspension => suspension.institutionID).includes(institutionID))
    throw new RegistrationSuspendedError();

  const productsNeedingRequirementCheck = products.filter(product => {
    const allowIgnoringRequirements =
      product.settings.allowSubscribingToWaitinglistWithoutMeetingRequirements ??
      product.settings.inheritedAllowSubscribingToWaitinglistWithoutMeetingRequirements?.[1];
    return !allowIgnoringRequirements;
  });
  const entryRequirementsByProduct = await loadUserEntryRequirementsStatus(
    user._id,
    productsNeedingRequirementCheck.map(p => p._id),
  );
  for (const product of productsNeedingRequirementCheck) {
    const result = entryRequirementsByProduct.get(product._id) ?? ({ requirementsMet: true } as const);
    if (!result.requirementsMet) {
      logger.error({ user, product, result }, 'User does not meet requirements');
      throw new ExpectedError('Sie erfüllen die Teilnahmevoraussetzungen für diese Veranstaltung nicht.');
    }
  }

  // check for payment method
  if (productsToBuy.some(product => product.hasPrice) && !paymentMethod)
    throw new Error('payment required but paymentmethod is undefined');

  // Create participations

  const participations: NarrowedEntityData<
    Participation,
    {
      _id: 1;
      state: 1;
      userID: 1;
      productID: 1;
      // user: { suspentions: { institutionID: 1 } };
      // product: { institutionID: 1 };
    }
  >[] = [];

  // get already booked timeslots
  const allUserParticipationsData = await loadRawEntityDataByQuery(
    Participation,
    { userID, state: { $in: [ParticipationState.participated, ParticipationState.in] } },
    { _id: 1, _class: 1, productID: 1, timeslotID: 1 },
  );
  const allUserProducts = await loadRawEntityDataByIDs(
    Product,
    allUserParticipationsData.map(pd => pd.productID),
    {
      _id: 1,
      leftCapacity: 1,
      appointments: { _id: 1, start: 1, end: 1, isNonBlocking: 1 },
      slots: { _id: 1, start: 1, end: 1, isNonBlocking: 1 },
    },
  );
  const allUserTimeslots = allUserParticipationsData.flatMap(_participation => {
    const product = allUserProducts.find(p => p._id === _participation.productID);
    if (!product)
      throw new Error(`product not productID=${_participation.productID}, participationID=${_participation._id}`);
    const participation = { ..._participation, product };
    return getParticipationTimespan(participation as any);
  });

  const checkForOverlappingTimeSlots = async (testSlots: TimeSlot[]) => {
    testSlots = testSlots.filter(slot => slot.start && slot.end); // filter out 'tbd' slots
    if (testSlots.length === 0) return;

    // check testslots against each other
    if (areTimeSlotOverlapping(testSlots)) throw new TimeslotsAreoverlappingError();

    const ss1 = participations.flatMap(p => getParticipationTimespan(p as any));
    const ss2 = allUserTimeslots;
    const timeslots = [...ss1, ...ss2].reduce((acc, timeslot) => {
      // filter out duplicates
      if (acc.find(_timeslot => _timeslot._id === timeslot._id)) return acc;
      if (testSlots.find(_timeslot => _timeslot._id === timeslot._id)) return acc;
      return [...acc, timeslot];
    }, [] as IDateTimeslot[]);

    // test against all timeslots
    // NOTE: test individually so we do not report overlappings between two already booked appointments
    for (const testSlot of testSlots) {
      for (const timeslot of timeslots) {
        if (areTimeSlotOverlapping([testSlot, timeslot as TimeSlot])) throw new TimeslotsAreoverlappingError();
      }
    }
  };

  await Promise.all(
    productData.map(async productDataItem => {
      // participation may already exists because user could have canceled a previous registration
      const getOrCreateParticipation = async () => {
        const productID = productDataItem.productID;

        const filter =
          productDataItem instanceof RegistrationEinzelberatungData
            ? { userID, productID, timeslotID: productDataItem.timeslotID }
            : { userID, productID };

        const existingParticipation = (await loadRawMongoData(Participation, filter))[0];
        if (existingParticipation)
          switch (existingParticipation.state) {
            case ParticipationState.in:
              logger.warn({ existingParticipation }, 'skipping - already enrolled');
              return null;
            case ParticipationState.waiting:
              logger.warn({ existingParticipation }, 'skipping - already on waiting list');
              return null;
            case ParticipationState.suspended:
              logger.error({ existingParticipation }, 'can not order - suspended');
              throw new Error("participation state is 'suspended' cant order again");
            case ParticipationState.left:
              // this is fine
              break;
            default:
              throw new Error('unreachable');
          }

        if (productDataItem instanceof RegistrationWorkshopData) {
          const participation = existingParticipation
            ? existingParticipation
            : {
                _id: A.getGloballyUniqueID(),
                _class: ParticipationWorkshop._class,
                ...filter,
                productID: filter.productID as string,
                nachrueckPriority: `low` as const,
                state: ParticipationState.waiting,
                product: null,
                waitingListPlace: null,
                ikh_doNotSendInvoice: false,
                createdAt: new Date(),
                updatedAt: new Date(),
              };

          // ! this is not proper plugin usage
          if (paymentPlugin.id === 'payment::sd') {
            // internal studium-digitale should not get invoices
            if (user?.email.endsWith('@studiumdigitale.uni-frankfurt.de')) participation.ikh_doNotSendInvoice = true;
          }

          // set state
          const workshop = await loadRawEntityDataByIDOrFail(Workshop, productID as string, {
            _id: 1,
            _class: 1,
            institutionID: 1,
            isCanceled: 1,
            capacity: 1,
            leftCapacity: 1,
            waitingHighPriorityParticipantsCount: 1,
            nachrueckBlockTimeForLowPriorityInDays: 1,
            requireManualAdmission: 1,
            appointments: { _id: 1, start: 1, end: 1, isNonBlocking: 1 },
            settings: {
              blockAutomaticMoveUpDaysInAdvance: 1,
              blockSignInHoursInAdvance: 1,
              blockActiveRegistrationHoursInAdvance: 1,
              meetingInfoMailLeadTime: 1,
            },
          });
          workshop.settings = await getMergedProductSettingsCached(workshop._id, [
            'blockAutomaticMoveUpDaysInAdvance',
            'blockSignInHoursInAdvance',
            'blockActiveRegistrationHoursInAdvance',
            'meetingInfoMailLeadTime',
          ]);
          workshop.leftCapacity = await calculateWorkshopLeftCapacity(workshop);

          // Check if registration is blocked due to timing constraint
          if (!canSignInForProduct(workshop)) {
            throw new BlockSignInHoursInAdvanceError();
          }

          participation.nachrueckPriority = await getWaitinglistPriorityForUserForProduct(userID, workshop._id);
          participation.state = ParticipationState.waiting;
          if (!workshop.requireManualAdmission) {
            if (
              workshop.leftCapacity > 0 &&
              canActivelyRegisterForProduct(workshop) &&
              (participation.nachrueckPriority === 'high' ||
                (participation.nachrueckPriority === 'low' && lowPriorityMayActivelyRegister(workshop)))
            )
              participation.state = ParticipationState.in;
            else {
              const highPriorityWaitingCount = await countWaitingByWorkshopServer(workshop._id, 'high');
              if (highPriorityWaitingCount > 0)
                void 0; // prevent stealing seats from currently waiting participants
              else if (participation.nachrueckPriority === 'low')
                void 0; // low-priority users always start on the waitinglist
              else if (nachrueckPossible({ ...participation, product: workshop }))
                participation.state = ParticipationState.in;
            }
          }

          // check for overlaping appointments
          const testSlots = getParticipationTimespan({ ...participation, product: workshop });
          await checkForOverlappingTimeSlots(
            testSlots.map(ts => new TimeSlot({ ...ts, start: ts.start, end: ts.end })),
          );

          // Take the max over all numeric places, matching the unique partial index, so stale places on
          // non-waiting rows cannot collide; the nachrück logic renumbers later.
          const maxRetries = 3;
          for (let attempt = 0; attempt < maxRetries; attempt++) {
            if (participation.state === ParticipationState.waiting) {
              const participationCollection = mongo().collection<Participation>(Participation._collectionName);
              const result = await participationCollection
                .aggregate([
                  { $match: { productID: workshop._id, waitingListPlace: { $type: 'number' } } },
                  { $group: { _id: null, waitingListPlace: { $max: '$waitingListPlace' } } },
                ])
                .toArray();

              participation.waitingListPlace = (() => {
                const maxPlace = result[0]?.waitingListPlace;
                if (maxPlace) return maxPlace + 1;
                else return 1;
              })();
            }

            try {
              await saveEntityData(participation);
              break;
            } catch (err: any) {
              const isDuplicateWaitingListPlace = err?.code === 11000 && err?.message?.includes('waitingListPlace');
              if (isDuplicateWaitingListPlace && attempt < maxRetries - 1) {
                logger.warn({ attempt, productID: workshop._id }, 'waitingListPlace conflict, retrying');
                continue;
              }
              throw err;
            }
          }

          // send meeting info mail if auto-mails were already sent
          if (participation.state === ParticipationState.in)
            sendMeetingInfoMailsIfAlreadySent(participation._id, workshop._id);

          return { ...participation, product: workshop };
        }

        if (productDataItem instanceof RegistrationEinzelberatungData) {
          const participation = existingParticipation
            ? (existingParticipation as EntityData<ParticipationEinzelberatung>)
            : {
                _id: A.getGloballyUniqueID(),
                _class: ParticipationEinzelberatung._class,
                ...filter,
                state: ParticipationState.waiting,
                timeslotID: filter.timeslotID as string,
                productID: filter.productID as string,
                createdAt: new Date(),
                updatedAt: new Date(),
              };

          participation.state = ParticipationState.in;

          // check if slot is still free
          const pCol = mongo().collection(ParticipationEinzelberatung._collectionName);
          const otherExistingParticipations = await pCol.raw
            .find<ParticipationEinzelberatung>({ timeslotID: participation.timeslotID })
            .toArray()
            .then(ps => ps.filter(p => p._id !== participation._id));

          const slotIsAlreadyFilled = otherExistingParticipations.some(
            p => p.state === ParticipationState.in || p.state === ParticipationState.participated,
          );
          if (slotIsAlreadyFilled)
            throw new ExpectedError('Dieser Termin ist leider bereits vergeben. Bitte wählen Sie einen anderen Termin.');

          // delete other participations (so users cant try to reregister again)
          await pCol.deleteMany({ _id: { $in: otherExistingParticipations.map(p => p._id as any) } });

          // check for overlaping appointments
          const product = await loadRawEntityDataByIDOrFail(Einzelberatung, productID as string, {
            _id: 1,
            _class: 1,
            slots: { _id: 1, start: 1, end: 1, isNonBlocking: 1 },
            slotConfig: { start: 1 },
            settings: { blockSignInHoursInAdvance: 1 },
          });
          const productSettings = await getMergedProductSettingsCached(productID as string, [
            'blockSignInHoursInAdvance',
          ]);
          product.settings = productSettings;

          // Check if registration is blocked due to timing constraint
          if (!canSignInForProduct(product, new Date())) {
            throw new BlockSignInHoursInAdvanceError();
          }

          const testSlots = getParticipationTimespan({
            _class: participation._class,
            timeslotID: participation.timeslotID,
            product,
          });
          await checkForOverlappingTimeSlots(
            testSlots.map(ts => new TimeSlot({ ...ts, start: ts.start, end: ts.end })),
          );

          await saveEntityData(participation);

          // send meeting info mail if auto-mail dispatch date is already in the past
          sendEinzelberatungMeetingInfoMailsIfAlreadySent(
            participation._id,
            productID as string,
            participation.timeslotID,
          );

          return { ...participation, product };
        }

        throw new Error('unreachable; unknown productDataItem: ' + productDataItem?._class);
      };

      const p = await getOrCreateParticipation();
      if (p) participations.push(p);
    }),
  );

  if (participations.length === 0) {
    logger.warn({ userID, productData }, 'all products were already booked - nothing to do');
    const loginToken = await generateLoginToken({ userID: user._id, userClass: User._class, ttu: 1 });
    return new BookingResult({ loginToken, paymentRequired: false, participationIDs: [] });
  }

  // check for suspensions
  for (const p of participations) {
    const product = await getProductByID(p.productID)({ _id: 1, institutionID: 1 });
    if (!product) throw new Error('could not find product ' + p.productID);
    failWhenSuspended({ ...p, user, product });
  }

  // add participant role
  await runInContext(serverContext.sessionID, async () => {
    await assignRoleToUser(user._id, Role.PARTICIPANT, [institutionID], User as any);
    notifyUserPermissionChange({ userID: user._id, kind: 'roles' });
  });

  const prices = await Promise.all(productsToBuy.map(p => paymentPlugin.getProductPrice(p._id, user._id)));
  const totalPrice = prices.map(p => p?.unitAmount ?? 0).reduce((sum, amount) => sum + amount, 0);
  const paymentRequired = totalPrice > 0;

  if (paymentRequired)
    void 0; // in case payment is required => send Mails after payment
  else await notifyBestaetigungWorkshopRegistrierung(institution._id, user, participations);

  // prepare result
  const participationIDs = participations.map(participation => participation._id);
  const loginToken = await generateLoginToken({ userID: user._id, userClass: User._class, ttu: 1 });
  const result = new BookingResult({ loginToken, paymentRequired, participationIDs });

  // maybe create payment
  if (paymentRequired) {
    if (!paymentMethod) throw new Error('payment required but paymentmethod is undefined');
    const paymentIDs = await createPayment(institutionID, participationIDs, paymentMethod);
    result.paymentIDs = paymentIDs;
  }

  // NOTE: Waitinglist updates for new bookings are now handled by the change stream listener in waitinglist-monitoring.ts
  // The change stream will automatically detect the new participation and trigger nachrueck logic

  // notify plugin
  await Promise.all(
    participations.map(async participation => {
      if (participation.state === ParticipationState.in) await paymentPlugin.afterEnrollment(participation._id);
    }),
  );

  return result;
}

/**
 * Can be called multiple times for one booking session (i.e. if user requires authentication)
 * @returns loginToken to allow client to login user to show booking results in profile
 */
export const book = registerMutation({
  operationName: 'book',
  params: [
    { name: 'productData', type: () => [RegistrationProductData] },
    { name: 'paymentMethod', type: () => String },
  ],
  type: () => BookingResult,
  resolve: (productData, paymentMethod) => async (gqlFields: BaseQLSelectionSet<BookingResult>) => {
    const user = await getCurrentUserServer();
    if (!user) throw new Error('user has to be logged in to book');

    // ensure authenticated session
    // allow server context to book for any user
    if (!context.serverContext && user._id !== context.userID) throw new RegistrationAuthorizationNeededError(user._id);

    await assertBookingEmailConfirmed(user._id, {
      serverContext: context.serverContext,
      impersonatingUser: (context as typeof context & { impersonatingUser?: string }).impersonatingUser,
    });
    await assertWorkshopRegistrationOpen(
      productData?.map(data => data.productID),
      {
        serverContext: context.serverContext,
        impersonatingUser: (context as typeof context & { impersonatingUser?: string }).impersonatingUser,
      },
    );

    return _book(user, productData, paymentMethod);
  },
});

export async function generatePaymentLinkForBooking(
  paymentID: string,
  paymentMethods: Stripe.Checkout.SessionCreateParams.PaymentMethodType[] | null = null,
) {
  const user = await getCurrentUserServer();
  const payment = await getBHPaymentByID(paymentID)({
    ...selectionSet(() => Payment, 2, ['statusUpdates']),
    _id: 1,
    participationIDs: 1,
    products: { product: { _id: 1 }, priceID: 1 },
    participations: {
      _id: 1,
      productID: 1,
      product: {
        _id: 1,
        name: 1,
        description: 1,
        institutionID: 1,
        isPaymentNeeded: 1,
        hasPrice: 1,
        prices: { ...selectionSet(() => Price, 2), _id: 1 },
      },
    },
  } as any);
  if (!payment) throw new Error('payment not found ' + paymentID);

  const participations = payment.participations;
  if (!participations.length) return null;

  const productIDs = participations.map(it => it?.productID as Maybe<string>).filter(isDefined);
  const products = participations.map(it => it?.product).filter(isDefined);
  const bundles = await getValidBundlesForProducts(productIDs)(selectionSet(() => Bundle, 2));
  const selectedBundles = getSelectedBundles(bundles, productIDs);

  if (products.length === 0) return null;
  const institutionID = products[0]!.institutionID;

  // handle suspensions for maybe already existing users
  if (!user) throw new Error('no user found');
  if (user.suspentions.map(suspension => suspension.institutionID).includes(institutionID))
    throw new RegistrationSuspendedError();

  const payProductsJSON = products
    .filter(product => product.isPaymentNeeded)
    .map(product => {
      const bundle = selectedBundles.find(bundle => bundle.productIDs.includes(product._id));
      const chosenProduct = payment.products.find(p => p.product._id === product._id);
      if (!chosenProduct) throw new Error('unreachable');
      const bundlePriceIndex = product.prices.findIndex(
        price => price instanceof BundlePrice && price.bundleID === bundle?._id,
      );
      const priceID = (() => {
        if (bundlePriceIndex !== -1) {
          const price = product.prices[bundlePriceIndex];
          if (!price) throw new Error('price not found ' + bundlePriceIndex);
          return price._id;
        }
        return chosenProduct.priceID;
      })();

      return { amount: 1, product: product as Product, priceID };
    });

  const stripeCustomerID = await ensureStripeCustomerForUser(user._id);

  if (!payProductsJSON.length) return null;
  const paymentLink = await payment.createPaymentLink({
    paymentMethods,
    products: payProductsJSON,
    mode: 'payment',
    createInvoice: true,
    savePaymentMethod: true,
    successURL: `/registration?${new URLSearchParams({ step: 'paid', institution: institutionID }).toString()}`,
    cancelURL: `/registration?${new URLSearchParams({
      step: 'paid',
      institution: institutionID,
      canceledPayment: '1',
    }).toString()}`,
    customerID: stripeCustomerID,
    automaticTax: true,
    collectTaxID: true,
    requireBillingAddress: true,
  });

  return paymentLink;
}

const _retryPayment = registerMutation({
  operationName: 'retryPayment',
  type: () => String,
  params: [{ name: 'paymentID', type: () => String }],
  resolve: (paymentID: string) => {
    return async () => {
      // TODO
      const payment = (await runInContext(serverContext.sessionID, () =>
        getBHPaymentByID(paymentID)({
          ...selectionSet(() => BHPayment, 2, ['statusUpdates']),
          products: { ...selectionSet(() => ChosenProduct, 2), product: { ...selectionSet(() => BasicProduct, 2) } },
        } as any),
      )) as BHPayment | null;
      if (!payment) throw new Error('_retryPayment:no payment found');

      const payProductsJSON = payment.products;
      const productID = payment.products[0]?.product._id;
      if (!productID) throw new Error('_retryPayment:no product found');
      const fullProduct = await loadRawEntityDataByIDOrFail(Product, productID, { institutionID: 1 });
      const institutionID = fullProduct.institutionID;

      const userID = payment.userReference.userID;
      if (!userID) throw new Error('_retryPayment:no userID found');
      const stripeCustomerID = await ensureStripeCustomerForUser(userID);

      return runInContext(serverContext.sessionID, () =>
        payment.createPaymentLink({
          paymentMethods: null,
          products: payProductsJSON,
          mode: 'payment',
          createInvoice: true,
          savePaymentMethod: true,
          successURL: `/registration?${new URLSearchParams({
            step: 'paid',
            // data: JSON.stringify(registrationData.toJSON()),
            institution: institutionID,
          }).toString()}`,
          cancelURL: `/registration?${new URLSearchParams({
            step: 'paid',
            // data: JSON.stringify(registrationData.toJSON()),
            institution: institutionID,
            canceledPayment: '1',
          }).toString()}`,
          customerID: stripeCustomerID,
          automaticTax: true,
          collectTaxID: true,
          requireBillingAddress: true,
        }),
      );
    };
  },
});

export async function retryPayment(paymentID: string) {
  const paymentLink = await _retryPayment(paymentID)();
  goTo(paymentLink);
}

export const getRegistrationDataFromPaymentID = registerQuery({
  operationName: 'getRegistrationDataFromPaymentID',
  type: () => RegistrationData,
  params: [{ name: 'paymentID', type: () => String }],
  resolve: (paymentID: string) => {
    return async (gqlFields: BaseQLSelectionSet<RegistrationData>) => {
      const payment = await runInContext(serverContext.sessionID, () =>
        getBHPaymentByID(paymentID)(selectionSet(() => BHPayment, 2, ['statusUpdates']) as any),
      );
      if (!payment) throw new Error('getRegistrationDataFromPaymentID:no payment found');
      const user = await runInContext(serverContext.sessionID, () =>
        getUserByID(payment.userReference.userID ?? 'NO_USER')(selectionSet(() => User, 2)),
      );
      if (!user) throw new Error('getRegistrationDataFromPaymentID:no user found');
      const participations = await runInContext(serverContext.sessionID, () =>
        getParticipationsByIDs(payment.participationIDs)({
          _id: 1,
          _class: 1,
          userID: 1,
          state: 1,
          createdAt: 1,
          updatedAt: 1,
          productID: 1,
          product: { institutionID: 1 },
          ...{ timeslotID: 1 },
          user: { _id: 1, email: 1, firstName: 1, lastName: 1, title: 1 },
        }),
      );
      if (!participations.length) throw new Error('getRegistrationDataFromPaymentID:no participations found');

      const institutionID = participations[0]?.product?.institutionID;
      if (!institutionID) throw new Error('getRegistrationDataFromPaymentID:no institution ID found');

      const userStammdaten = await runInContext(serverContext.sessionID, () =>
        getStammdatenByUserAndInstitution(user._id, institutionID)(UserStammdaten.allFields),
      );
      if (!userStammdaten) throw new Error('getRegistrationDataFromPaymentID:no user stammdaten found');

      return new RegistrationData({
        institutionID,
        productData: participations.map(participation => {
          if (participation instanceof ParticipationEinzelberatung)
            return new RegistrationEinzelberatungData({
              productID: participation.productID,
              timeslotID: participation.timeslotID,
            });
          return new RegistrationWorkshopData({ productID: participation.productID });
        }),
        acceptedTerms: true, // couldn't get here else
        userStammdaten,
        user,
      });
    };
  },
});
